import { Button, Empty, Skeleton, Subheading } from '@treams/treams-ui';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import { sortBy } from 'lodash';

import { DialogueIcon } from '@treams/treams-icons';

import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import FeedbackComposerCompetencyCard from './FeedbackComposerCompetencyCard';
import {
	Competency,
	Goal,
	JobPosition,
	LocalizedString,
	useFeedbackComposerCompetenciesQuery,
} from 'generated/graphql';

type CompetencySelectorItem = [string, Competency[]];

function jobPositionTitle(
	position: JobPosition,
	localize: (text: LocalizedString) => { text: string },
): string {
	const jobLevel = localize(position.jobLevel?.title ?? [])?.text;
	return `${localize(position.jobTitle.title).text} ${
		position.jobLevel ? `- ${jobLevel}` : ''
	}`.toLowerCase();
}

function competencySelectorItemsFromJobPositions(
	jobPositions: JobPosition[],
	localize: (text: LocalizedString) => { text: string },
): CompetencySelectorItem[] {
	return sortBy(
		jobPositions.map((position) => [
			jobPositionTitle(position, localize),
			position.competencies.map(({ competency }) => competency),
		]),
		([title]) => title,
	);
}

function competencySelectorItemsFromGoals(
	goals: Goal[],
	localize: (text: LocalizedString) => { text: string },
): CompetencySelectorItem[] {
	return goals.reduce((acc, goal) => {
		if (!goal.competency?.competency) {
			return acc;
		}

		return [...acc, [localize(goal.title).text, [goal.competency.competency]]];
	}, []);
}

function toggleSelectedCompetency(
	competencies: { competencyID: string; text?: string }[],
	competency: Competency,
	selected: boolean,
) {
	if (selected) {
		return [...competencies, { competencyID: competency.id }];
	}

	return competencies.filter((c) => c.competencyID !== competency.id);
}

export default function FeedbackComposerCompetencies(props: {
	userID: string;
	competencies: { competencyID: string; text?: string }[];
	onChange: (competencies: { competencyID: string; text?: string }[]) => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const query = useFeedbackComposerCompetenciesQuery({
		variables: {
			userID: props.userID,
		},
	});

	const competencySelectorItems = [
		...competencySelectorItemsFromGoals((query.data?.user.goals as Goal[]) ?? [], localize),
		...competencySelectorItemsFromJobPositions(
			(query.data?.user.jobPositions as JobPosition[]) ?? [],
			localize,
		),
	];

	const onChangeHandler = (competency: Competency, isSelected: boolean) => {
		props.onChange(toggleSelectedCompetency(props.competencies, competency, isSelected));
	};

	return (
		<Box>
			<Flex py={3} alignItems="center">
				<Box flexGrow={1} flexShrink={1}>
					<Subheading>{t('feedback.composer.competencies.title')}</Subheading>
				</Box>
				<Button
					small
					type="secondary"
					title={t('feedback.composer.competencies.selectAll')}
					onClick={() => {
						props.onChange(
							query.data?.user.jobPositions
								.flatMap((position) => position.competencies)
								.map(({ competency }) => competency)
								.map(({ id }) => ({ competencyID: id })) ?? [],
						);
					}}
				/>
			</Flex>
			<HorizontalRule />
			{query.data ? (
				<Box>
					{competencySelectorItems.length === 0 && (
						<Skeleton.Empty
							mt={2}
							contents={
								<Empty
									small
									icon={<DialogueIcon />}
									title={t('feedback.composer.competencies.empty.jobPositions.title')}
								/>
							}
						>
							<Skeleton.Box height={100} />
						</Skeleton.Empty>
					)}
					{competencySelectorItems.map(([title, competenciesToSelect]) => (
						<Box key={title} mt={3}>
							<Subheading>{title}</Subheading>
							{competenciesToSelect.map((competency) => {
								const isSelected = props.competencies.find(({ competencyID }) => {
									return competency.id === competencyID;
								});

								return (
									<FeedbackComposerCompetencyCard
										key={competency.id}
										competency={competency}
										selected={Boolean(isSelected)}
										onChange={(isSelected) => onChangeHandler(competency, !isSelected)}
									/>
								);
							})}
						</Box>
					))}
				</Box>
			) : (
				<Box>
					<Spinner isLoading size="18px" />
				</Box>
			)}
		</Box>
	);
}
